<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ClientPortalsAPI from 'dashboard/api/clientPortals';
import WebsiteLayout from '../../website/components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ResponsiveTable from '../../website/components/ResponsiveTable.vue';
import ClientPortalsForm from './ClientPortalsForm.vue';

const { t } = useI18n();

const portals = ref([]);
const loading = ref(false);
const showForm = ref(false);
const selectedPortal = ref(null);

const headerTitle = computed(() => 'Portails Clients'); // TODO: Add translation key

const fetchPortals = async () => {
  loading.value = true;
  try {
    const response = await ClientPortalsAPI.get();
    portals.value = response.data;
  } catch (error) {
    useAlert('Failed to fetch portals'); // TODO: Translation
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchPortals();
});

const openCreateForm = () => {
  selectedPortal.value = null;
  showForm.value = true;
};

const openEditForm = portal => {
  selectedPortal.value = portal;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedPortal.value = null;
};

const handleSave = async () => {
  await fetchPortals();
  closeForm();
};

const deletePortal = async portal => {
  if (!confirm('Are you sure you want to delete this portal?')) return; // TODO: Translation

  try {
    await ClientPortalsAPI.delete(portal.id);
    useAlert('Portal deleted successfully'); // TODO: Translation
    await fetchPortals();
  } catch (error) {
    useAlert('Failed to delete portal'); // TODO: Translation
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString * 1000).toLocaleDateString();
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        label="Nouveau Portail"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="portals.length === 0"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center"
      >
        <span class="i-lucide-library-big size-10 text-n-purple-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">Aucun portail</h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        Créez votre premier portail client pour commencer.
      </p>
      <Button
        label="Nouveau Portail"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <div
      v-else
      class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                Nom
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                Slug
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                Date de création
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <tr
              v-for="portal in portals"
              :key="portal.id"
              class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50"
            >
              <td class="px-5 py-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ portal.name }}
                </span>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                <a
                  :href="`/portal/${portal.slug}`"
                  target="_blank"
                  class="flex items-center gap-1 text-n-blue-11 hover:underline"
                >
                  /{{ portal.slug }}
                  <span class="i-lucide-external-link size-3" />
                </a>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ formatDate(portal.created_at) }}
              </td>
              <td
                class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex justify-end gap-2">
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    title="Edit"
                    @click="openEditForm(portal)"
                  >
                    <span class="i-lucide-edit size-4" />
                  </button>
                  <router-link
                    :to="{
                      name: 'client_portals_edit',
                      params: { portalId: portal.id },
                    }"
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-emerald-3 hover:bg-n-emerald-4 text-n-emerald-11 hover:text-n-emerald-12 border border-n-emerald-6 hover:border-n-emerald-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    title="Edit Dashboard"
                  >
                    <span class="i-lucide-layout-dashboard size-4" />
                  </router-link>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    title="Delete"
                    @click="deletePortal(portal)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <ClientPortalsForm
      v-if="showForm"
      :portal="selectedPortal"
      @close="closeForm"
      @save="handleSave"
    />
  </WebsiteLayout>
</template>
